import React, { useMemo } from "react"
import ECharts from "echarts-for-react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { formatMessage } from "@/utils/format-message"
import { langStore } from "@/stores/lang-store"
import { statsStore } from "../../stats-store"
import useIsMobile from "@/hooks/use-is-mobile"
import { statsBundle } from "../../stats-const"

const HeatmapChart = ({ title = "", dong, xLabels, yLabels, data, visualMax }) => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { region, aptCode } = useParams()
  const [searchParams] = useSearchParams()
  const resource = searchParams.get("resource")

  const digit = Math.max(1, Math.floor(Math.log10(visualMax)) + 1)
  const visualMapPadding = digit * 8 + 24

  const { lang } = langStore()
  const { unit } = statsStore()

  const mappedYLabels = useMemo(() => {
    return yLabels
      .map((label, index) => ({
        label: parseInt(label, 10).toString(),
        originalIndex: index
      }))
      .sort((a, b) => parseInt(a.label, 10) - parseInt(b.label, 10))
  }, [yLabels])

  const height = yLabels.length * (isMobile ? 5 : 6)

  const sortedYLabels = useMemo(() => {
    return mappedYLabels.map((item) => item.label)
  }, [mappedYLabels])

  const option = useMemo(() => {
    const oldToNewYIndex = {}
    mappedYLabels.forEach((item, newIndex) => {
      oldToNewYIndex[item.originalIndex] = newIndex
    })

    const sortedData = data.map((item) => [item[0], oldToNewYIndex[item[1]], item[2]])

    return {
      title: {
        text: title,
        left: "center",
        top: 8
      },
      tooltip: {
        position: "top",
        formatter: (p) => {
          const date = xLabels[p.data[0]]
          const ho = sortedYLabels[p.data[1]]
          const value = p.data[2]?.toFixed(2) ?? 0

          return `
            ${formatMessage(statsBundle.ho[lang], { ho })}
            <br/>${date}: <b>${value} ${unit}</b>`
        }
      },
      grid: {
        top: 50,
        left: isMobile ? 50 : 0,
        right: isMobile ? 10 : 40 + visualMapPadding,
        bottom: 0
      },
      xAxis: {
        type: "category",
        data: xLabels,
        axisLabel: {
          rotate: 0,
          hideOverlap: true
        }
      },
      yAxis: {
        type: "category",
        data: sortedYLabels.map((ho) => {
          return formatMessage(statsBundle.ho[lang], { ho })
        }),
        axisLabel: {
          rotate: 0,
          hideOverlap: false
        }
      },
      visualMap: {
        show: !isMobile,
        min: 0,
        max: visualMax,
        calculable: !isMobile,
        orient: "vertical",
        align: "left",
        itemHeight: 400,
        top: "center",
        right: 0,
        inRange: {
          color: ["#FFFFCF", "#D8F7FF", "#61ACF1", "#6B5CE7", "#4D11D6"]
        }
      },
      series: [
        {
          type: "heatmap",
          data: sortedData,
          emphasis: {
            itemStyle: {
              shadowBlur: 6,
              shadowColor: "rgba(0,0,0,0.3)"
            }
          }
        }
      ]
    }
  }, [
    xLabels,
    data,
    visualMax,
    title,
    isMobile,
    lang,
    unit,
    visualMapPadding,
    mappedYLabels,
    sortedYLabels
  ])

  const onEvents = useMemo(
    () => ({
      dblclick: (params) => {
        if (!dong || !region || !aptCode) return

        const ho = sortedYLabels[params?.data?.[1]]
        if (!ho) return

        navigate(`/apt/${region}/${aptCode}/units?resource=${resource}&dong=${dong}&ho=${ho}`)
      }
    }),
    [aptCode, dong, navigate, region, resource, sortedYLabels]
  )

  return (
    <div className="md:min-h-50" style={{ height }}>
      <ECharts
        className="duration-300 transition"
        notMerge
        option={option}
        style={{ width: "100%", height: "100%" }}
        onEvents={onEvents}
      />
    </div>
  )
}

export default HeatmapChart
